import { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, storage, auth } from '../firebase';

/* ── AUTH CONFIG ── */
const ALLOWED_EMAIL = 'pritamprg102847@gmail.com';

/* ── TYPES ── */
interface CarDoc {
  id: string;
  reg_number: string;
  year: string;
  month: string;
  veh_model: string;
  veh_price: number;
  veh_fuel_type: string;
  veh_transmission_type: string;
  veh_kms_driven: string;
  veh_ownership: string;
  veh_engine_capacity: string;
  veh_insurance: string;
  veh_insaurance_valid: string;
  veh_key_type: string;
  veh_seat_type: string;
  veh_available_at: string;
  veh_music_player: string;
  veh_abs: boolean;
  veh_air_bag: boolean;
  veh_central_locking: boolean;
  veh_climate_control: boolean;
  veh_reverse_camera: boolean;
  veh_spare_key: boolean;
  veh_sunroof: boolean;
  veh_facebook: string;
  veh_instagram: string;
  name: string;
  make: string;
  model: string;
  img: string[];
}

const EMPTY: Omit<CarDoc, 'id'> = {
  reg_number: '', year: '', month: '', veh_model: '', veh_price: 0,
  veh_fuel_type: 'Petrol', veh_transmission_type: 'Manual',
  veh_kms_driven: '', veh_ownership: 'First', veh_engine_capacity: '',
  veh_insurance: '', veh_insaurance_valid: '', veh_key_type: 'Smart Key',
  veh_seat_type: 'Fabric', veh_available_at: '', veh_music_player: '',
  veh_abs: false, veh_air_bag: false, veh_central_locking: false,
  veh_climate_control: false, veh_reverse_camera: false,
  veh_spare_key: false, veh_sunroof: false,
  veh_facebook: '', veh_instagram: '',
  name: '', make: '', model: '', img: [],
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function AdminPage() {
  /* ── auth state ── */
  const [authed, setAuthed]           = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [email, setEmail]             = useState('');
  const [password, setPassword]       = useState('');
  const [loginErr, setLoginErr]       = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  /* ── data state ── */
  const [cars, setCars]           = useState<CarDoc[]>([]);
  const [loading, setLoading]     = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<CarDoc | null>(null);
  const [form, setForm]           = useState<Omit<CarDoc, 'id'>>(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [deleteId, setDeleteId]   = useState<string | null>(null);
  const [search, setSearch]       = useState('');

  /* ── image state ── */
  const [imgUrls, setImgUrls]               = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<{ name: string; progress: number }[]>([]);

  /* ── listen to Firebase auth state on mount ── */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user && user.email === ALLOWED_EMAIL) {
        setAuthed(true);
      } else {
        setAuthed(false);
        if (user) signOut(auth); // signed in but wrong email — boot them
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  /* ── fetch cars ── */
  const fetchCars = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'cars'));
      setCars(snap.docs.map(d => ({ id: d.id, ...d.data() } as CarDoc)));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) fetchCars(); }, [authed]);

  /* ── login ── */
  const handleLogin = async () => {
    setLoginErr('');
    if (!email.trim() || !password.trim()) {
      setLoginErr('Please enter your email and password.');
      return;
    }
    if (email.trim() !== ALLOWED_EMAIL) {
      setLoginErr('Access denied. Unauthorized email address.');
      return;
    }
    setLoginLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // onAuthStateChanged above handles setting authed = true
    } catch (err: any) {
      const msg: Record<string, string> = {
        'auth/wrong-password':     'Incorrect password.',
        'auth/user-not-found':     'No account found for this email.',
        'auth/too-many-requests':  'Too many failed attempts. Try again later.',
        'auth/invalid-credential': 'Invalid email or password.',
        'auth/network-request-failed': 'Network error. Check your connection.',
      };
      setLoginErr(msg[err.code] || 'Login failed. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  /* ── logout ── */
  const handleLogout = async () => {
    await signOut(auth);
    setAuthed(false);
  };

  /* ── modal helpers ── */
  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setImgUrls([]);
    setUploadingFiles([]);
    setModalOpen(true);
  };

  const openEdit = (car: CarDoc) => {
    setEditing(car);
    const { id, ...rest } = car;
    setForm(rest);
    setImgUrls(Array.isArray(car.img) ? car.img : []);
    setUploadingFiles([]);
    setModalOpen(true);
  };

  /* ── save ── */
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        img: imgUrls,
        veh_price: Number(form.veh_price),
        timestamp: serverTimestamp(),
      };
      if (editing) {
        await updateDoc(doc(db, 'cars', editing.id), payload);
      } else {
        await addDoc(collection(db, 'cars'), payload);
      }
      setModalOpen(false);
      fetchCars();
    } finally {
      setSaving(false);
    }
  };

  /* ── delete ── */
  const handleDelete = async (id: string) => {
    await deleteDoc(doc(db, 'cars', id));
    setDeleteId(null);
    fetchCars();
  };

  /* ── image upload ── */
  const handleImageUpload = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach((file) => {
      const fileName = `cars/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      const task = uploadBytesResumable(storageRef, file);

      setUploadingFiles(prev => [...prev, { name: file.name, progress: 0 }]);

      task.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadingFiles(prev =>
            prev.map(f => f.name === file.name ? { ...f, progress: pct } : f)
          );
        },
        (err) => {
          console.error('Upload failed:', err);
          setUploadingFiles(prev => prev.filter(f => f.name !== file.name));
        },
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          setImgUrls(prev => [...prev, url]);
          setUploadingFiles(prev => prev.filter(f => f.name !== file.name));
        }
      );
    });
  };

  /* ── remove image ── */
  const removeImage = async (url: string) => {
    setImgUrls(prev => prev.filter(u => u !== url));
    try {
      await deleteObject(ref(storage, url));
    } catch { /* external URL or already deleted */ }
  };

  const filtered = cars.filter(c =>
    `${c.name} ${c.reg_number} ${c.veh_model} ${c.make} ${c.model}`
      .toLowerCase().includes(search.toLowerCase())
  );

  const f = (key: keyof Omit<CarDoc, 'id'>, value: any) =>
    setForm(prev => ({ ...prev, [key]: value }));

  /* ════════════════════════════════════
     AUTH LOADING SCREEN
  ════════════════════════════════════ */
  if (authLoading) return (
    <div style={{ minHeight: '100vh', background: '#080808', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <div style={loginLogoMark}>A</div>
        <div style={{ color: '#333', fontSize: '13px', letterSpacing: '0.05em' }}>Checking authentication…</div>
      </div>
    </div>
  );

  /* ════════════════════════════════════
     LOGIN SCREEN
  ════════════════════════════════════ */
  if (!authed) return (
    <div style={loginPage}>
      <div style={loginCard}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div style={loginLogoMark}>A</div>
        </div>
        <h1 style={loginTitle}>Admin Access</h1>
        <p style={loginSub}>Vehicle Inventory Management</p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '28px' }}>
          <div>
            <label style={loginLabel}>Email</label>
            <input
              style={loginInp}
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setLoginErr(''); }}
              placeholder="admin@example.com"
              autoComplete="email"
            />
          </div>
          <div>
            <label style={loginLabel}>Password</label>
            <input
              style={loginInp}
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setLoginErr(''); }}
              placeholder="••••••••"
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
          </div>

          {loginErr && (
            <div style={loginErrBox}>
              <span style={{ marginRight: '8px' }}>⚠</span>{loginErr}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={loginLoading}
            style={{ ...loginBtn, opacity: loginLoading ? 0.7 : 1 }}
          >
            {loginLoading ? 'Signing in…' : 'Sign In →'}
          </button>
        </div>

        <p style={{ textAlign: 'center', marginTop: '24px', fontSize: '11px', color: '#2a2a2a' }}>
          Protected by Firebase Authentication
        </p>
      </div>
    </div>
  );

  /* ════════════════════════════════════
     DASHBOARD
  ════════════════════════════════════ */
  return (
    <div style={page}>

      {/* ── HEADER ── */}
      <div style={header}>
        <div style={headerInner}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={loginLogoMark}>A</div>
            <div>
              <div style={headerTitle}>Admin Panel</div>
              <div style={headerSub}>{cars.length} vehicles in inventory</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' as const }}>
            <input
              placeholder="Search name, reg, model…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={searchInput}
            />
            <button onClick={openAdd} style={addBtn}>+ Add Car</button>
            <button onClick={handleLogout} style={logoutBtn}>Logout</button>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div style={statsBar}>
        <div style={statsBarInner}>
          {[
            ['Total',     cars.length,                                              '#fff'],
            ['Petrol',    cars.filter(c => c.veh_fuel_type === 'Petrol').length,   '#f90'],
            ['Diesel',    cars.filter(c => c.veh_fuel_type === 'Diesel').length,   '#4af'],
            ['Electric',  cars.filter(c => c.veh_fuel_type === 'Electric').length, '#f4f'],
            ['Available', cars.filter(c => c.veh_available_at).length,             '#4fa'],
          ].map(([label, val, color]) => (
            <div key={String(label)} style={statChip}>
              <span style={{ fontSize: '18px', fontWeight: 700, color: color as string }}>{val}</span>
              <span style={{ fontSize: '10px', color: '#444', letterSpacing: '0.05em' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div style={tableWrap}>
        {loading ? (
          <div style={emptyState}>Loading inventory…</div>
        ) : filtered.length === 0 ? (
          <div style={emptyState}>No vehicles found.</div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                {['', 'Vehicle', 'Reg No.', 'Year', 'Fuel', 'Transmission', 'KMs Driven', 'Price', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((car, i) => (
                <tr key={car.id} className="trow" style={{ background: i % 2 === 0 ? '#0c0c0c' : '#0f0f0f' }}>
                  <td style={{ ...td, width: '60px' }}>
                    {car.img?.[0] ? (
                      <img src={car.img[0]} alt="" style={rowThumb} />
                    ) : (
                      <div style={rowThumbEmpty}>No img</div>
                    )}
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: 600, color: '#eee', fontSize: '13px' }}>
                      {car.name || car.veh_model || `${car.make} ${car.model}`.trim() || '—'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#444', marginTop: '2px' }}>
                      {[car.make, car.model].filter(Boolean).join(' ') || '—'}
                    </div>
                  </td>
                  <td style={{ ...td, color: '#666', fontSize: '12px' }}>{car.reg_number || '—'}</td>
                  <td style={{ ...td, color: '#888', fontSize: '12px' }}>{car.year || '—'}</td>
                  <td style={td}><span style={fuelPill(car.veh_fuel_type)}>{car.veh_fuel_type || '—'}</span></td>
                  <td style={{ ...td, color: '#777', fontSize: '12px' }}>{car.veh_transmission_type || '—'}</td>
                  <td style={{ ...td, color: '#666', fontSize: '12px' }}>
                    {car.veh_kms_driven ? `${Number(car.veh_kms_driven).toLocaleString('en-IN')} km` : '—'}
                  </td>
                  <td style={{ ...td, color: '#FFD700', fontWeight: 700, fontSize: '13px' }}>
                    {car.veh_price ? `₹ ${(car.veh_price).toLocaleString('en-IN')}` : '—'}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' as const }}>
                    <button onClick={() => openEdit(car)} style={editBtn}>Edit</button>
                    <button onClick={() => setDeleteId(car.id)} style={delBtn}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ══════════════════════════════════════
          ADD / EDIT MODAL
      ══════════════════════════════════════ */}
      {modalOpen && (
        <div style={modalBackdrop} onClick={() => setModalOpen(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>

            <div style={modalHeader}>
              <span style={modalTitle}>{editing ? 'Edit Vehicle' : 'Add New Vehicle'}</span>
              <button onClick={() => setModalOpen(false)} style={closeBtn}>✕</button>
            </div>

            <div style={modalBody}>

              {/* ── BASIC INFO ── */}
              <FormSection label="Basic Info">
                <TwoCol>
                  <Field label="Display Name">
                    <input style={inp} value={form.name} onChange={e => f('name', e.target.value)} placeholder="e.g. Toyota Fortuner 2.8 GD-6" />
                  </Field>
                  <Field label="Make">
                    <input style={inp} value={form.make} onChange={e => f('make', e.target.value)} placeholder="Toyota" />
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Model">
                    <input style={inp} value={form.model} onChange={e => f('model', e.target.value)} placeholder="Fortuner" />
                  </Field>
                  <Field label="Variant / Veh Model">
                    <input style={inp} value={form.veh_model} onChange={e => f('veh_model', e.target.value)} placeholder="2.8 GD-6 4x4 AT" />
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Year">
                    <input style={inp} value={form.year} onChange={e => f('year', e.target.value)} placeholder="2021" />
                  </Field>
                  <Field label="Month">
                    <input style={inp} value={form.month} onChange={e => f('month', e.target.value)} placeholder="March" />
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Registration Number">
                    <input style={inp} value={form.reg_number} onChange={e => f('reg_number', e.target.value)} placeholder="MH-12-AB-1234" />
                  </Field>
                  <Field label="Price (₹)">
                    <input style={inp} type="string" value={form.veh_price || ''} onChange={e => f('veh_price', e.target.value)} placeholder="2850000" />
                  </Field>
                </TwoCol>
              </FormSection>

              {/* ── SPECIFICATIONS ── */}
              <FormSection label="Specifications">
                <TwoCol>
                  <Field label="Fuel Type">
                    <select style={inp} value={form.veh_fuel_type} onChange={e => f('veh_fuel_type', e.target.value)}>
                      {['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid', 'LPG'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Transmission">
                    <select style={inp} value={form.veh_transmission_type} onChange={e => f('veh_transmission_type', e.target.value)}>
                      {['Manual', 'Automatic', 'AMT', 'DCT', 'CVT'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="KMs Driven">
                    <input style={inp} value={form.veh_kms_driven} onChange={e => f('veh_kms_driven', e.target.value)} placeholder="42000" />
                  </Field>
                  <Field label="Ownership">
                    <select style={inp} value={form.veh_ownership} onChange={e => f('veh_ownership', e.target.value)}>
                      {['First', 'Second', 'Third', 'Fourth'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Engine Capacity">
                    <input style={inp} value={form.veh_engine_capacity} onChange={e => f('veh_engine_capacity', e.target.value)} placeholder="2800cc" />
                  </Field>
                  <Field label="Key Type">
                    <select style={inp} value={form.veh_key_type} onChange={e => f('veh_key_type', e.target.value)}>
                      {['Smart Key', 'Normal Key', 'Push Start', 'Remote Key'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Seat Type">
                    <select style={inp} value={form.veh_seat_type} onChange={e => f('veh_seat_type', e.target.value)}>
                      {['Fabric', 'Leather', 'Semi-Leather', 'Velvet'].map(o => <option key={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="Music / Infotainment">
                    <input style={inp} value={form.veh_music_player} onChange={e => f('veh_music_player', e.target.value)} placeholder="Android Auto / Apple CarPlay" />
                  </Field>
                </TwoCol>
              </FormSection>

              {/* ── INSURANCE & AVAILABILITY ── */}
              <FormSection label="Insurance & Availability">
                <TwoCol>
                  <Field label="Insurance Type">
                    <input style={inp} value={form.veh_insurance} onChange={e => f('veh_insurance', e.target.value)} placeholder="Comprehensive" />
                  </Field>
                  <Field label="Insurance Valid Till">
                    <input style={inp} value={form.veh_insaurance_valid} onChange={e => f('veh_insaurance_valid', e.target.value)} placeholder="Dec 2025" />
                  </Field>
                </TwoCol>
                <TwoCol>
                  <Field label="Available At">
                    <input style={inp} value={form.veh_available_at} onChange={e => f('veh_available_at', e.target.value)} placeholder="Pune, Maharashtra" />
                  </Field>
                </TwoCol>
              </FormSection>

              {/* ── FEATURES ── */}
              <FormSection label="Features & Safety">
                <div style={toggleGrid}>
                  {([
                    ['veh_abs',             'ABS'],
                    ['veh_air_bag',         'Airbag'],
                    ['veh_central_locking', 'Central Locking'],
                    ['veh_climate_control', 'Climate Control'],
                    ['veh_reverse_camera',  'Reverse Camera'],
                    ['veh_spare_key',       'Spare Key'],
                    ['veh_sunroof',         'Sunroof'],
                  ] as [keyof Omit<CarDoc, 'id'>, string][]).map(([key, label]) => (
                    <div
                      key={key}
                      style={{
                        ...toggleCard,
                        borderColor: form[key] ? '#3a3000' : '#1e1e1e',
                        background: form[key] ? '#1a1500' : '#141414',
                      }}
                      onClick={() => f(key, !form[key])}
                    >
                      <div style={{ ...toggleTrack, background: form[key] ? '#FFD700' : '#2a2a2a' }}>
                        <div style={{ ...toggleThumb, transform: form[key] ? 'translateX(16px)' : 'translateX(2px)' }} />
                      </div>
                      <span style={{ fontSize: '12px', color: form[key] ? '#FFD700' : '#555', fontWeight: form[key] ? 600 : 400 }}>
                        {label}
                      </span>
                    </div>
                  ))}
                </div>
              </FormSection>

              {/* ── IMAGES ── */}
              <FormSection label={`Images (${imgUrls.length} uploaded)`}>
                <label style={dropZone}>
                  <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                    onChange={e => handleImageUpload(e.target.files)} />
                  <div style={dropIconCircle}>↑</div>
                  <div style={{ fontSize: '13px', color: '#777', marginBottom: '3px' }}>Click to select images</div>
                  <div style={{ fontSize: '11px', color: '#3a3a3a' }}>PNG, JPG, WEBP — multiple allowed</div>
                </label>

                {uploadingFiles.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '8px', marginTop: '4px' }}>
                    {uploadingFiles.map(uf => (
                      <div key={uf.name} style={progressWrap}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#666', maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                            {uf.name}
                          </span>
                          <span style={{ fontSize: '11px', color: '#FFD700', fontWeight: 700 }}>{uf.progress}%</span>
                        </div>
                        <div style={progressTrack}>
                          <div style={{ ...progressFill, width: `${uf.progress}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {imgUrls.length > 0 && (
                  <div style={imgGrid}>
                    {imgUrls.map((url, i) => (
                      <div key={url} style={imgThumbWrap}>
                        <img src={url} alt={`car-${i + 1}`} style={imgThumbStyle} />
                        <div style={{ ...imgBadge, background: i === 0 ? '#FFD700' : 'rgba(0,0,0,0.7)', color: i === 0 ? '#000' : '#FFD700' }}>
                          {i === 0 ? 'Cover' : `#${i + 1}`}
                        </div>
                        <button style={imgDelBtn} onClick={() => removeImage(url)} title="Remove">✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {imgUrls.length === 0 && uploadingFiles.length === 0 && (
                  <div style={{ textAlign: 'center' as const, color: '#2a2a2a', fontSize: '12px', padding: '6px 0' }}>
                    No images yet — first image becomes the cover
                  </div>
                )}
              </FormSection>

              {/* ── SOCIAL ── */}
              <FormSection label="Social Links">
                <TwoCol>
                  <Field label="Facebook URL">
                    <input style={inp} value={form.veh_facebook} onChange={e => f('veh_facebook', e.target.value)} placeholder="https://facebook.com/post/..." />
                  </Field>
                  <Field label="Instagram URL">
                    <input style={inp} value={form.veh_instagram} onChange={e => f('veh_instagram', e.target.value)} placeholder="https://instagram.com/p/..." />
                  </Field>
                </TwoCol>
              </FormSection>

            </div>

            <div style={modalFooter}>
              <button onClick={() => setModalOpen(false)} style={cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={saveBtn} disabled={saving}>
                {saving ? 'Saving…' : editing ? '✓ Save Changes' : '+ Add Vehicle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          DELETE CONFIRM MODAL
      ══════════════════════════════════════ */}
      {deleteId && (
        <div style={modalBackdrop}>
          <div style={{ ...modalBox, maxWidth: '380px' }}>
            <div style={modalHeader}>
              <span style={{ ...modalTitle, color: '#ff5555' }}>Delete Vehicle?</span>
            </div>
            <div style={{ padding: '20px 24px', color: '#666', fontSize: '14px', lineHeight: 1.7 }}>
              This action is <strong style={{ color: '#ff5555' }}>permanent</strong> and cannot be undone.
              The vehicle will be removed from your inventory.
            </div>
            <div style={modalFooter}>
              <button onClick={() => setDeleteId(null)} style={cancelBtn}>Cancel</button>
              <button onClick={() => handleDelete(deleteId)} style={{ ...saveBtn, background: '#ff4444' }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        input::placeholder, textarea::placeholder { color: #2e2e2e; }
        select option { background: #1a1a1a; color: #ddd; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: #0a0a0a; }
        ::-webkit-scrollbar-thumb { background: #222; border-radius: 4px; }
        .trow:hover td { background: rgba(255,215,0,0.025) !important; }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════
   FORM SUB-COMPONENTS
══════════════════════════════════════ */
function FormSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '28px' }}>
      <div style={sectionLabelStyle}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '10px' }}>{children}</div>
    </div>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '5px' }}>
      <label style={fieldLabelStyle}>{label}</label>
      {children}
    </div>
  );
}

/* ══════════════════════════════════════
   FUEL PILL HELPER
══════════════════════════════════════ */
function fuelPill(fuel: string): React.CSSProperties {
  const bg:   Record<string, string> = { Petrol: '#2a1500', Diesel: '#001525', CNG: '#001a10', Electric: '#1a0025', Hybrid: '#0a1a00', LPG: '#1a1a00' };
  const text: Record<string, string> = { Petrol: '#f90',    Diesel: '#5af',    CNG: '#4fa',   Electric: '#c8f',    Hybrid: '#8f4',    LPG: '#ff6' };
  return {
    display: 'inline-block', fontSize: '11px', fontWeight: 600,
    padding: '3px 10px', borderRadius: '20px',
    background: bg[fuel] || '#1a1a1a',
    color: text[fuel] || '#aaa',
  };
}

/* ══════════════════════════════════════
   STYLES
══════════════════════════════════════ */

/* Login */
const loginPage:    React.CSSProperties = { minHeight: '100vh', background: '#080808', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const loginCard:    React.CSSProperties = { background: '#111', border: '1px solid #1e1e1e', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '380px' };
const loginLogoMark:React.CSSProperties = { width: '48px', height: '48px', background: '#FFD700', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 900, color: '#000' };
const loginTitle:   React.CSSProperties = { fontSize: '22px', fontWeight: 700, textAlign: 'center', margin: '0 0 4px', color: '#fff' };
const loginSub:     React.CSSProperties = { fontSize: '13px', color: '#333', textAlign: 'center', margin: 0 };
const loginLabel:   React.CSSProperties = { fontSize: '12px', color: '#444', display: 'block', marginBottom: '6px' };
const loginInp:     React.CSSProperties = { width: '100%', background: '#171717', border: '1px solid #252525', borderRadius: '8px', color: '#ddd', padding: '11px 14px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' };
const loginErrBox:  React.CSSProperties = { background: 'rgba(255,60,60,0.08)', border: '1px solid rgba(255,60,60,0.18)', color: '#ff6b6b', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', display: 'flex', alignItems: 'center' };
const loginBtn:     React.CSSProperties = { width: '100%', background: '#FFD700', color: '#000', border: 'none', borderRadius: '10px', padding: '13px', fontWeight: 700, fontSize: '15px', cursor: 'pointer', marginTop: '4px', transition: 'opacity 0.2s' };

/* Layout */
const page:         React.CSSProperties = { background: '#080808', minHeight: '100vh', color: '#fff', fontFamily: 'Segoe UI, system-ui, sans-serif' };
const header:       React.CSSProperties = { background: '#0d0d0d', borderBottom: '1px solid #1a1a1a', padding: '14px 24px', position: 'sticky', top: 0, zIndex: 50 };
const headerInner:  React.CSSProperties = { maxWidth: '1400px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' };
const headerTitle:  React.CSSProperties = { fontSize: '16px', fontWeight: 700, color: '#fff' };
const headerSub:    React.CSSProperties = { fontSize: '11px', color: '#444', marginTop: '1px' };
const searchInput:  React.CSSProperties = { background: '#171717', border: '1px solid #242424', borderRadius: '8px', color: '#ccc', padding: '8px 14px', fontSize: '13px', width: '240px', outline: 'none' };
const addBtn:       React.CSSProperties = { background: '#FFD700', color: '#000', border: 'none', borderRadius: '8px', padding: '8px 18px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' };
const logoutBtn:    React.CSSProperties = { background: 'transparent', color: '#444', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer' };

/* Stats */
const statsBar:      React.CSSProperties = { background: '#0a0a0a', borderBottom: '1px solid #141414', padding: '0 24px' };
const statsBarInner: React.CSSProperties = { maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '0' };
const statChip:      React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 24px', borderRight: '1px solid #141414', gap: '2px' };

/* Table */
const tableWrap:     React.CSSProperties = { overflowX: 'auto', padding: '20px 24px' };
const table:         React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
const th:            React.CSSProperties = { textAlign: 'left', padding: '10px 14px', fontSize: '10px', fontWeight: 600, color: '#444', letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid #181818', background: '#080808', whiteSpace: 'nowrap' };
const td:            React.CSSProperties = { padding: '12px 14px', borderBottom: '1px solid #111', verticalAlign: 'middle' };
const rowThumb:      React.CSSProperties = { width: '52px', height: '38px', objectFit: 'cover', borderRadius: '5px', display: 'block' };
const rowThumbEmpty: React.CSSProperties = { width: '52px', height: '38px', background: '#161616', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: '#333' };
const editBtn:       React.CSSProperties = { background: '#171717', border: '1px solid #252525', color: '#888', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer', marginRight: '6px' };
const delBtn:        React.CSSProperties = { background: 'rgba(255,50,50,0.07)', border: '1px solid rgba(255,50,50,0.18)', color: '#ff6666', borderRadius: '6px', padding: '5px 12px', fontSize: '12px', cursor: 'pointer' };
const emptyState:    React.CSSProperties = { padding: '80px', textAlign: 'center', color: '#2a2a2a', fontSize: '14px' };

/* Modal */
const modalBackdrop: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px' };
const modalBox:      React.CSSProperties = { background: '#111', border: '1px solid #1e1e1e', borderRadius: '14px', width: '100%', maxWidth: '700px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' };
const modalHeader:   React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid #1a1a1a', flexShrink: 0 };
const modalTitle:    React.CSSProperties = { fontSize: '16px', fontWeight: 700, color: '#fff' };
const closeBtn:      React.CSSProperties = { background: 'none', border: 'none', color: '#444', fontSize: '18px', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' };
const modalBody:     React.CSSProperties = { padding: '24px', overflowY: 'auto', flex: 1 };
const modalFooter:   React.CSSProperties = { padding: '16px 24px', borderTop: '1px solid #1a1a1a', display: 'flex', justifyContent: 'flex-end', gap: '10px', flexShrink: 0 };
const cancelBtn:     React.CSSProperties = { background: 'transparent', border: '1px solid #252525', color: '#666', borderRadius: '8px', padding: '9px 20px', fontSize: '13px', cursor: 'pointer' };
const saveBtn:       React.CSSProperties = { background: '#FFD700', color: '#000', border: 'none', borderRadius: '8px', padding: '9px 24px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };

/* Form */
const inp:              React.CSSProperties = { width: '100%', background: '#161616', border: '1px solid #222', borderRadius: '8px', color: '#ccc', padding: '9px 12px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' };
const sectionLabelStyle:React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: '#3a3a3a', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #181818' };
const fieldLabelStyle:  React.CSSProperties = { fontSize: '11px', color: '#444', letterSpacing: '0.02em' };

/* Toggles */
const toggleGrid:  React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: '8px' };
const toggleCard:  React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', border: '1px solid #1e1e1e', cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s' };
const toggleTrack: React.CSSProperties = { width: '34px', height: '20px', borderRadius: '10px', position: 'relative', flexShrink: 0, transition: 'background 0.2s' };
const toggleThumb: React.CSSProperties = { position: 'absolute', top: '2px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'transform 0.2s' };

/* Image upload */
const dropZone:     React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #222', borderRadius: '10px', padding: '24px 20px', cursor: 'pointer', background: '#131313', gap: '4px' };
const dropIconCircle:React.CSSProperties = { width: '36px', height: '36px', borderRadius: '50%', background: '#1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: '#FFD700', marginBottom: '6px' };
const progressWrap: React.CSSProperties = { background: '#141414', border: '1px solid #1e1e1e', borderRadius: '8px', padding: '10px 14px' };
const progressTrack:React.CSSProperties = { height: '3px', background: '#1e1e1e', borderRadius: '2px', overflow: 'hidden' };
const progressFill: React.CSSProperties = { height: '100%', background: '#FFD700', borderRadius: '2px', transition: 'width 0.15s ease' };
const imgGrid:      React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px', marginTop: '4px' };
const imgThumbWrap: React.CSSProperties = { position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1e1e1e', aspectRatio: '4/3' };
const imgThumbStyle:React.CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const imgBadge:     React.CSSProperties = { position: 'absolute', top: '5px', left: '5px', fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '4px' };
const imgDelBtn:    React.CSSProperties = { position: 'absolute', top: '5px', right: '5px', background: 'rgba(220,30,30,0.85)', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 };